import { Badge } from "@elaniintc/goes-design-system";
import { createColumnHelper, type CellContext } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo } from "react";
import type { AnnouncementType } from "@/types/entities/announcements";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import { Menu } from "./components/notification-menu";
import { mapStatusToDisplay } from "./notifications.helpers";

const columnHelper = createColumnHelper<AnnouncementType>();

export const useNotificationColumns = ({
  canEdit,
  canDelete,
  onEdit,
  onToggleStatus,
  onDelete,
  onCellButtonClick,
}: {
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (notification: AnnouncementType) => void;
  onToggleStatus: (notification: AnnouncementType) => void;
  onDelete: (notification: AnnouncementType) => void;
  onCellButtonClick: (notification: AnnouncementType) => void;
}) => {
  const buildTitleCell = (info: CellContext<AnnouncementType, unknown>) => {
    const { title, content } = info.row.original;
    return (
      <TableLeadingCell
        title={title}
        subtitle={content}
        onButtonClick={() => onCellButtonClick(info.row.original)}
      />
    );
  };

  const buildStatusCell = (info: CellContext<AnnouncementType, string>) => {
    const status = mapStatusToDisplay(info.getValue());
    if (status === "Publicada") {
      return (
        <Badge variant="success" shape="pill" size="sm">
          Publicada
        </Badge>
      );
    }
    if (status === "Borrador") {
      return (
        <Badge variant="action" shape="pill" size="sm">
          Borrador
        </Badge>
      );
    }
    if (status === "Despublicada") {
      return (
        <Badge variant="warning" shape="pill" size="sm">
          Despublicada
        </Badge>
      );
    }
    if (status === "Archivada") {
      return (
        <Badge variant="neutral" shape="pill" size="sm">
          Archivada
        </Badge>
      );
    }
    return "-";
  };

  const buildUpdatedAtCell = (info: CellContext<AnnouncementType, string>) => {
    const date = info.getValue();
    return date ? (
      <div className="text-body text-base font-normal leading-7">
        {dayjs(date).format("MMM DD, YYYY | HH:mm A")}
      </div>
    ) : (
      "-"
    );
  };

  const buildActionsCell = (info: CellContext<AnnouncementType, unknown>) => {
    return (
      <div className="w-full flex justify-end">
        <Menu
          notification={info.row.original}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
        />
      </div>
    );
  };

  return useMemo(() => {
    return [
      columnHelper.accessor("title", {
        id: "title",
        header: "TÍTULO & DESCRIPCIÓN",
        meta: {
          className: "w-[350px] min-w-[300px] max-w-[350px]",
          cellClassName: "group",
        },
        cell: buildTitleCell,
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: "ESTATUS",
        meta: {
          className: "hidden md:table-cell",
        },
        cell: buildStatusCell,
      }),
      columnHelper.accessor("updated_at", {
        id: "updated_at",
        header: "ÚLTIMA ACTUALIZACIÓN",
        meta: {
          className: "hidden sm:table-cell",
        },
        cell: buildUpdatedAtCell,
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          className: "w-[56px]",
        },
        cell: buildActionsCell,
      }),
    ].filter(({ id }) => {
      if (!canEdit && !canDelete) return id !== "actions";
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, canDelete, onEdit, onToggleStatus, onDelete, onCellButtonClick]);
};
